<script setup lang="ts">
/**
 * 帳號管理（`/admin-users`，`meta.roles: [superadmin]`）。
 *
 * 三張卡對應帳號的三種處境（批80 批C）：
 * - **待首登**＝已預建（`active && !has_logged_in`）：等對方自己用 Google 登入。
 *   **系統不寄邀請信**（LS 沒有郵件設施），所以預建成功後要提示操作者自行通知。
 * - **待啟用**＝對方自己先登入過但還沒啟用（`!active`）：批79 就有的流程。
 *   ★ 批108 用語：這裡的「啟用」是**員工帳號**的開關（`admin_users.active`），與授權的
 *     「審核核發」是兩件事——同一個詞在兩個頁面代表兩件事會讓人問錯問題。
 * - **已啟用**：角色下拉、顯示名稱行內編輯、最後登入、操作紀錄連結、強制登出。
 *
 * 認證一律走 Google OAuth——這裡**沒有**「建密碼帳號」這回事，預建只是先把 email
 * 與角色登記好（`google_sub` 留空），首登時後端會撿同一列回填。
 *
 * 批93-C 加了第四個角色 **代理商（reseller）**，它與我方三級**不同族**：
 * - 建立時必須指定所屬**代理商公司**（後端不變式：`role==='reseller'` ⇔ `reseller_id` 非 null）
 * - **不能用我方 Workspace 網域**（後端 422）——代理商請用他自家的 Google 帳號
 * - **staff ↔ reseller 不可互轉**（後端 422「請刪除後重建」）→ 角色下拉只給同族選項
 * - 產品範圖（`product_keys`）：**我方角色**由 UI 勾選，不勾＝送 null＝不限。
 *   **代理商角色刻意不給勾**（批97 Bug 2）——一律送 `null`＝**沿用公司範圍**
 *   （有效範圖＝帳號層 ∩ 公司層，`core/scope.py::effective_scope`）。原本兩層都能勾，
 *   結果是切換角色／公司後殘留的勾選被送出、超出公司範圍 → 後端 422
 *   `scope_exceeds_reseller`。要再收窄某位窗口請走 API，見 `docs/ADMIN_ROLES.md` §2。
 */
import { computed, onMounted, ref, watch } from 'vue'

import { vEnter } from '@/lib/enterKey'
import ProductScopeField from '@/components/ProductScopeField.vue'
import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import * as ls from '@/api/ls'
import type { AdminUserRow } from '@/api/ls'
import { ROLE_OPTIONS, STAFF_ROLE_OPTIONS, roleLabel } from '@/lib/roles'
import { fmtSeen } from '@/lib/staleness'
import type { RoleName } from '@/stores/auth'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const auth = useAuthStore()
const dialogs = useDialogsStore()
const store = useDataStore()

const rows = ref<AdminUserRow[]>([])
const myEmail = ref('')
const loading = ref(true)

/** 待首登：預建（active）但從未 Google 登入過。 */
const invited = computed(() => rows.value.filter((u) => u.active && !u.has_logged_in))
const pending = computed(() => rows.value.filter((u) => !u.active))
const activeUsers = computed(() => rows.value.filter((u) => u.active && u.has_logged_in))

async function load() {
  loading.value = true
  try {
    const [me, list] = await Promise.all([ls.getMe(), ls.listAdminUsers()])
    myEmail.value = me.email
    rows.value = list
  } finally {
    loading.value = false
  }
}
onMounted(async () => {
  // 代理商公司下拉與產品範圖勾選都靠這兩份清單（本頁是 superadmin，兩支都讀得到）。
  await Promise.all([load(), store.ensureResellers(), store.ensureProducts().catch(() => {})])
})

function errText(e: unknown, fallback: string): string {
  const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (d == null) return fallback
  if (typeof d === 'string') return d
  const obj = d as { code?: string; role?: string; active?: boolean; message?: string }
  if (obj.code === 'exists') {
    return `這個 email 已經有帳號了（角色 ${roleLabel(obj.role ?? '')}、${obj.active ? '已啟用' : '待啟用'}）。`
  }
  // 批107：後端的驗證錯誤都帶 `message`（`reseller_inactive`／`scope_exceeds_reseller`…）
  // ——原本一律 `JSON.stringify` 把整包 dict 丟到畫面上，使用者看到的是一串 JSON。
  if (obj.message) return obj.message
  return JSON.stringify(d)
}

async function handle(fn: () => Promise<unknown>, fallback: string) {
  try {
    await fn()
    await load()
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: errText(e, fallback) })
  }
}

const open = (u: AdminUserRow) => handle(() => ls.patchAdminUser(u.id, { active: true }), '啟用失敗')
const deactivate = (u: AdminUserRow) => handle(() => ls.patchAdminUser(u.id, { active: false }), '停用失敗')
const setRole = (u: AdminUserRow, role: string) => handle(() => ls.patchAdminUser(u.id, { role }), '變更角色失敗')

async function remove(u: AdminUserRow) {
  if (!(await dialogs.confirm({ message: `刪除帳號 ${u.email}？`, danger: true, confirmText: '刪除' }))) return
  await handle(() => ls.deleteAdminUser(u.id), '刪除失敗')
}

// 角色中文名與下拉選項一律取自 `@/lib/roles`（批97 Bug 4：畫面文案的唯一真相；
// 我方三級與後端 `ROLE_RANK` 同一組值，代理商不在那份階序裡＝rank 0）。

// --- 預建（邀請）---
/**
 * 新增帳號時的預設角色（批110）。營運主管只開得了代理商窗口，預設留 `admin` 會讓下拉
 * 顯示空白（那個選項被 `roleOptions` 濾掉了），看起來像壞掉。
 */
function defaultNewRole(): RoleName {
  return auth.isSuperadmin ? 'admin' : 'reseller'
}

const form = ref<{
  email: string
  role: string
  name: string
  reseller_id: string
  product_keys: string[]
}>({ email: '', role: defaultNewRole(), name: '', reseller_id: '', product_keys: [] })
const inviting = ref(false)
const isResellerRole = computed(() => form.value.role === 'reseller')

/**
 * 這一列**我改不改得動**（批110）——與後端 `admin.py::_gate_admin_user_target` 同一套規則：
 * superadmin 全放行；manager 只碰得到代理商窗口（`role === 'reseller'`）或自己。
 *
 * ★ UI 只是「不給白按」，真閘門在後端（打 API 一樣 403）。但這裡不藏起來而是**反灰**：
 *   營運主管看得到我方員工名冊是刻意的（他要知道還有誰有權限），只是動不了。
 */
function canEditRow(u: { role: string; email: string }): boolean {
  if (auth.isSuperadmin) return true
  return u.role === 'reseller' || u.email === myEmail.value
}

/** 新增帳號時 manager 只能開代理商窗口（我方員工帳號是 superadmin 的事）。 */
const roleOptions = computed(() =>
  auth.isSuperadmin ? ROLE_OPTIONS : ROLE_OPTIONS.filter((o) => o.value === 'reseller'),
)
/** 選中的代理商公司（產品範圍照它顯示；未選＝null）。 */
const pickedCompany = computed(
  () => store.resellers.find((r) => r.id === form.value.reseller_id) ?? null,
)
/** 公司範圍的中文；`product_keys` 為 null／空＝不限（建立公司時後端強制非空，理論上不會發生）。 */
const companyScopeText = computed(() => {
  const keys = pickedCompany.value?.product_keys
  if (!keys?.length) return '不限'
  return keys.map((k) => store.productName(k)).join('、')
})
/**
 * 可勾的產品。**代理商角色回空陣列**（批97 Bug 2：整塊勾選 UI 換成公司範圍唯讀顯示）
 * ——不是「收斂成公司範圖」，而是根本不給帳號層勾。
 */
const scopeChoices = computed(() => (isResellerRole.value ? [] : store.products))
// 切換角色或代理商公司時清掉殘留勾選：不清的話，切成代理商前勾的產品會被一起送出，
// 超出公司範圍就是後端 422 `scope_exceeds_reseller`（畫面上完全看不出勾了什麼）。
watch(
  () => [form.value.role, form.value.reseller_id],
  () => {
    form.value.product_keys = []
  },
)
function toggleScope(key: string) {
  form.value.product_keys = form.value.product_keys.includes(key)
    ? form.value.product_keys.filter((k) => k !== key)
    : [...form.value.product_keys, key]
}
/** 空陣列＝**不限**（送 null）；有勾＝限定那幾個。刻意不讓「一個都不能碰」用 UI 表達。 */
const canInvite = computed(
  () => !!form.value.email.trim() && (!isResellerRole.value || !!form.value.reseller_id),
)

async function invite() {
  const email = form.value.email.trim()
  if (!email || !canInvite.value) return
  inviting.value = true
  try {
    await ls.createAdminUser({
      email,
      role: form.value.role,
      name: form.value.name.trim() || null,
      reseller_id: isResellerRole.value ? form.value.reseller_id : null,
      // 代理商一律 null＝沿用公司範圍（批97 Bug 2）；我方角色不勾也是 null＝不限。
      product_keys:
        isResellerRole.value || !form.value.product_keys.length
          ? null
          : [...form.value.product_keys],
    })
    const wasReseller = isResellerRole.value
    form.value = { email: '', role: defaultNewRole(), name: '', reseller_id: '', product_keys: [] }
    await load()
    await dialogs.notify({
      title: '已新增帳號',
      message: wasReseller
        ? `已新增 ${email.toLowerCase()}。系統不會寄信——請通知對方用「這個 Google 帳號」登入後台，登入後只會看到代理商的三個頁面。`
        : `已新增 ${email.toLowerCase()}。系統不會寄信——請自行通知對方用該 Google 帳號登入後台，首次登入就會直接進來（不需要再啟用）。`,
    })
  } catch (e) {
    await dialogs.notify({ title: '新增帳號失敗', message: errText(e, '新增帳號失敗') })
  } finally {
    inviting.value = false
  }
}

// --- 編輯帳號（批107）------------------------------------------------------
//
// 原本只有「角色下拉」與「顯示名稱行內編輯」兩個零碎入口，產品範圍完全改不了
// （`docs/ADMIN_ROLES.md` 當時寫「要收窄請走 API」）。這裡補一個 modal 把可改的
// 欄位收在一起：名稱、角色（同族）、啟用、所屬公司（代理商窗口）、產品範圍。
//
// ★ **email 不可改**：它是 Google 登入的身分鍵，也是稽核的 target_id。改成別人的
//   email 等於把帳號連同歷史稽核一起交給另一個人——要換人就刪掉重建（主人裁示）。
// ★ 送出時**只送有改的欄位**：後端對「自己」仍擋角色與啟用的變更，整份送上來會被 403。
const editing = ref<AdminUserRow | null>(null)
const editForm = ref<{
  name: string
  role: string
  active: boolean
  reseller_id: string
  product_keys: string[] | null
}>({ name: '', role: 'admin', active: true, reseller_id: '', product_keys: null })
const editSaving = ref(false)
const isEditSelf = computed(() => !!editing.value && editing.value.email === myEmail.value)
const isEditReseller = computed(() => editForm.value.role === 'reseller')
/** 編輯中的窗口所屬公司（產品範圍的可勾選項要限縮成它的範圍）。 */
const editCompany = computed(
  () => store.resellers.find((r) => r.id === editForm.value.reseller_id) ?? null,
)

function openEdit(u: AdminUserRow) {
  editing.value = u
  editForm.value = {
    name: u.name ?? '',
    role: u.role,
    active: u.active,
    reseller_id: u.reseller_id ?? '',
    product_keys: u.product_keys ? [...u.product_keys] : null,
  }
}
// 換公司時把範圍重設回「沿用公司範圍」：留著上一家的勾選，送出就是 422
// `scope_exceeds_reseller`（批97 Bug 2 的老路）。
watch(
  () => editForm.value.reseller_id,
  (next, prev) => {
    if (prev !== undefined && next !== prev) editForm.value.product_keys = null
  },
)

async function saveEdit() {
  const u = editing.value
  if (!u) return
  const body: Record<string, unknown> = {}
  const name = editForm.value.name.trim() || null
  if (name !== (u.name ?? null)) body.name = name
  if (!isEditSelf.value) {
    if (editForm.value.role !== u.role) body.role = editForm.value.role
    if (editForm.value.active !== u.active) body.active = editForm.value.active
  }
  if (isEditReseller.value && editForm.value.reseller_id !== (u.reseller_id ?? '')) {
    body.reseller_id = editForm.value.reseller_id
  }
  const nextKeys = editForm.value.product_keys
  const curKeys = u.product_keys ?? null
  const same =
    nextKeys === null
      ? curKeys === null
      : curKeys !== null &&
        nextKeys.length === curKeys.length &&
        nextKeys.every((k) => curKeys.includes(k))
  if (!same) body.product_keys = nextKeys
  if (!Object.keys(body).length) {
    editing.value = null
    return
  }
  editSaving.value = true
  try {
    await ls.patchAdminUser(u.id, body)
    editing.value = null
    await load()
  } catch (e) {
    await dialogs.notify({ title: '儲存失敗', message: errText(e, '儲存帳號失敗') })
  } finally {
    editSaving.value = false
  }
}

/**
 * 清單上的範圖顯示。
 * - 我方角色：null／缺席＝**不限**
 * - 代理商：null＝**沿用公司範圍**（批97 Bug 2 起帳號層一律不填；印「不限」會誤導成
 *   「這個窗口什麼產品都能碰」，實際仍被公司層 ∩ 掉）
 */
function scopeText(u: AdminUserRow): string {
  if (!u.product_keys) {
    if (u.role !== 'reseller') return '不限'
    const keys = store.resellers.find((r) => r.id === u.reseller_id)?.product_keys
    if (!keys?.length) return '沿用公司範圍'
    return `沿用公司：${keys.map((k) => store.productName(k)).join('、')}`
  }
  if (!u.product_keys.length) return '（無）'
  return u.product_keys.map((k) => store.productName(k)).join('、')
}
function companyText(u: AdminUserRow): string {
  if (u.role !== 'reseller') return '—'
  return u.reseller_name ?? store.resellerName(u.reseller_id)
}

// --- 顯示名稱行內編輯 ---
const editingName = ref<string | null>(null)
const nameDraft = ref('')

function startEditName(u: AdminUserRow) {
  editingName.value = u.id
  nameDraft.value = u.name ?? ''
}
async function saveName(u: AdminUserRow) {
  // Enter 存完之後 input 被移除還會噴一次 blur → 用「還在編輯中嗎」擋掉重複 PUT。
  if (editingName.value !== u.id) return
  const next = nameDraft.value.trim()
  editingName.value = null
  if (next === (u.name ?? '')) return
  await handle(() => ls.patchAdminUser(u.id, { name: next || null }), '修改顯示名稱失敗')
}

// --- 強制登出 ---
const busyLogout = ref<string | null>(null)

async function forceLogout(u: AdminUserRow) {
  const isSelf = u.email === myEmail.value
  const ok = await dialogs.confirm({
    title: '強制登出',
    message: isSelf
      ? '作廢你自己現在之前簽出的所有後台 token。⚠️ 這會把你自己也登出（包含這個視窗），要重新用 Google 登入。適用情境＝在別台電腦忘了登出。'
      : `作廢 ${u.email} 現在之前簽出的所有後台 token（該帳號的所有裝置都要重新登入）。帳號本身不會被停用或降權。`,
    danger: true,
    confirmText: '強制登出',
  })
  if (!ok) return
  busyLogout.value = u.id
  try {
    await ls.revokeAdminUserTokens(u.id)
    if (isSelf) {
      // 自己被踢：下一個請求就會 401 → client.ts 清 token 導回登入頁。這裡先講清楚。
      await dialogs.notify({ title: '已強制登出', message: '你自己的 session 也失效了，接下來會回到登入頁。' })
    }
    await load()
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: errText(e, '強制登出失敗') })
  } finally {
    busyLogout.value = null
  }
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>帳號管理</h1>
      <span class="spacer" />
      <RouterLink class="tab" :to="{ name: 'audit' }">稽核紀錄</RouterLink>
    </div>

    <div v-if="loading" class="loading-wrap"><span class="spinner" /></div>

    <template v-else>
      <!-- 預建（邀請）-->
      <div class="card">
        <div class="card-head"><h3 class="section-title">新增帳號</h3></div>
        <p class="subtle">
          先把 email 與角色登記好，對方用該 Google 帳號登入後台就直接進來（不必再啟用）。
          <strong>系統不會寄信</strong>，請自行通知對方。任何 Google 帳號都可以（不限我方網域）。
        </p>
        <form class="invite" @submit.prevent="invite">
          <label class="hf">
            <span>Email</span>
            <input v-model="form.email" type="text" placeholder="someone@example.com" />
          </label>
          <label class="hf">
            <span>角色</span>
            <select v-model="form.role">
              <option v-for="o in roleOptions" :key="o.value" :value="o.value">
                {{ o.label }}
              </option>
            </select>
          </label>
          <label v-if="isResellerRole" class="hf">
            <span>代理商公司</span>
            <select v-model="form.reseller_id">
              <option value="">請選擇公司…</option>
              <option v-for="r in store.resellers" :key="r.id" :value="r.id" :disabled="!r.active">
                {{ r.name }}{{ r.active ? '' : '（已停權）' }}
              </option>
            </select>
          </label>
          <label class="hf">
            <span>顯示名稱</span>
            <input v-model="form.name" type="text" placeholder="（可留空，首登時抓 Google 名稱）" />
          </label>
          <!-- 與旁邊的輸入框同高（36px）：獨立表單一律用預設尺寸，`sm` 只給表格列內的行內操作。 -->
          <UiButton variant="primary" :loading="inviting" :disabled="!canInvite">
            新增帳號
          </UiButton>
        </form>
        <p v-if="isResellerRole" class="subtle rs-hint">
          代理商請用<strong>其自家的 Google 帳號</strong>（不能用我方 Workspace 網域，後端會擋）。
          公司還沒建的話請先到
          <RouterLink :to="{ name: 'resellers' }">〈代理商〉</RouterLink> 建立並圈定產品範圍。
        </p>
        <p v-if="isResellerRole && !store.resellers.length" class="warn-text">
          ⚠ 目前沒有任何代理商公司——請先建立一家。
        </p>

        <!-- 產品範圖。我方角色＝可勾（不勾＝不限，不是「都不能碰」）；
             ★ 代理商角色刻意**不給勾**（批97 Bug 2）：帳號層一律送 null＝沿用公司範圍，
               這裡只把公司範圍唯讀顯示出來。要再收窄某位窗口請走 API。 -->
        <div class="scope-block">
          <template v-if="!isResellerRole">
            <span class="scope-label">產品範圖</span>
            <label
              v-for="p in scopeChoices"
              :key="p.key"
              class="pick"
              :class="{ on: form.product_keys.includes(p.key) }"
            >
              <input
                type="checkbox"
                :checked="form.product_keys.includes(p.key)"
                @change="toggleScope(p.key)"
              />
              {{ p.name }}
            </label>
            <span class="subtle">
              {{ form.product_keys.length ? '只能處理勾選的產品' : '不勾＝不限（全部產品）' }}
            </span>
          </template>
          <template v-else>
            <span class="scope-label">產品範圍</span>
            <template v-if="pickedCompany">
              <span v-for="k in pickedCompany.product_keys ?? []" :key="k" class="chip">
                {{ store.productName(k) }}
              </span>
              <span class="subtle">
                沿用公司範圍（{{ companyScopeText }}）——帳號層不可在此縮小。
              </span>
            </template>
            <span v-else class="subtle">請先選擇代理商公司，才知道這個帳號的產品範圍。</span>
          </template>
        </div>
      </div>

      <!-- 待首登（已預建）-->
      <div v-if="invited.length" class="card">
        <div class="card-head"><h3 class="section-title">待首登（{{ invited.length }}）</h3></div>
        <p class="subtle">已新增、等對方用 Google 登入。登入後會自動移到「已啟用」。</p>
        <div class="tbl-scroll" data-hscroll>
          <table v-card-table class="tbl card-table">
            <!-- 批113 ⑨：與「已啟用」表同步，代理商＋產品範圖併成一欄兩行（8→7 欄）。 -->
            <thead>
              <tr>
                <th>Email</th><th>角色</th><th>代理商・產品範圖</th>
                <th>顯示名稱</th><th>新增者</th><th>新增時間</th><th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="u in invited" :key="u.id">
                <td class="mail">{{ u.email }}</td>
                <td class="nowrap">{{ roleLabel(u.role) }}</td>
                <td class="scope">
                  <div>{{ companyText(u) }}</div>
                  <div class="sub scope-line">{{ scopeText(u) }}</div>
                </td>
                <td class="name-cell">{{ u.name ?? '—' }}</td>
                <td class="mail">{{ u.invited_by ?? '—' }}</td>
                <td class="nowrap">{{ u.created_at.slice(0, 10) }}</td>
                <td class="acts">
                  <UiButton variant="secondary" size="sm" @click="openEdit(u)">編輯</UiButton>
                  <UiButton variant="danger" size="sm" @click="remove(u)">刪除</UiButton>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 待啟用 -->
      <div v-if="pending.length" class="card">
        <div class="card-head"><h3 class="section-title">待啟用（{{ pending.length }}）</h3></div>
        <p class="subtle">這些帳號已用 Google 登入過，但尚未啟用，無法進入後台。</p>
        <div class="tbl-scroll" data-hscroll>
          <table v-card-table class="tbl card-table">
            <thead><tr><th>Email</th><th>顯示名稱</th><th>建立時間</th><th></th></tr></thead>
            <tbody>
              <tr v-for="u in pending" :key="u.id">
                <td>{{ u.email }}</td>
                <td>{{ u.name ?? '—' }}</td>
                <td>{{ u.created_at.slice(0, 10) }}</td>
                <td class="acts">
                  <UiButton variant="primary" size="sm" @click="open(u)">啟用</UiButton>
                  <UiButton variant="danger" size="sm" @click="remove(u)">拒絕</UiButton>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 已啟用 -->
      <div class="card">
        <div class="card-head"><h3 class="section-title">已啟用（{{ activeUsers.length }}）</h3></div>
        <div class="tbl-scroll" data-hscroll>
          <table v-card-table class="tbl card-table">
            <!--
              批113 ⑨（1366 跑版）：8 欄 → **6 欄**。1366×768 下 `.card` 內只有 1068px，
              而操作欄五顆按鈕就吃掉 323px，剩下的分給 8 欄 → 顯示名稱／產品範圖被壓到 80px
              折成三四行、`（你）` 掉行，右邊還得橫捲才看得到。照授權清單 9→6 的手法把
              **天然成對**的兩組併成一欄兩行（第二行 `.sub`）：代理商・產品範圖、最後登入・建立時間。
              欄位一個都沒少，只是改成縱向排（主人裁示 5：按鈕不減、不新做下拉元件）。
            -->
            <thead>
              <tr>
                <th>Email</th><th>顯示名稱</th><th>角色</th><th>代理商・產品範圖</th>
                <th>最後登入・建立</th><th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="u in activeUsers" :key="u.id">
                <td class="mail">
                  {{ u.email }}
                  <!-- 徽章而不是「（你）」兩個字：窄欄時括號文字會被推到第二行，
                       看起來像多出一列空白；`inline-block` 的 pill 則整塊跟著 email 走。 -->
                  <span v-if="u.email === myEmail" class="me-badge">你</span>
                </td>
                <td class="name-cell">
                  <template v-if="editingName === u.id">
                    <input
                      v-model="nameDraft"
                      class="name-in sm"
                      type="text"
                      v-enter="() => saveName(u)"
                      @keyup.esc="editingName = null"
                      @blur="saveName(u)"
                    />
                  </template>
                  <button v-else type="button" class="name-btn" @click="startEditName(u)">
                    {{ u.name ?? '（未填）' }}
                  </button>
                </td>
                <td>
                  <!-- ★ 只在**同族之內**給換：staff↔reseller 互轉後端 422（要刪除後重建），
                       下拉裡放一個註定失敗的選項只會讓人以為是 bug。 -->
                  <select
                    v-if="u.email !== myEmail && u.role !== 'reseller' && auth.isSuperadmin"
                    class="sm"
                    :value="u.role"
                    @change="setRole(u, ($event.target as HTMLSelectElement).value)"
                  >
                    <option v-for="o in STAFF_ROLE_OPTIONS" :key="o.value" :value="o.value">
                      {{ o.label }}
                    </option>
                  </select>
                  <span
                    v-else
                    :title="u.role === 'reseller' ? '代理商與我方角色不可互轉——要改請刪除後重建' : undefined"
                  >
                    {{ roleLabel(u.role) }}
                  </span>
                </td>
                <td class="scope">
                  <div>{{ companyText(u) }}</div>
                  <div class="sub scope-line">{{ scopeText(u) }}</div>
                </td>
                <td class="when">
                  <div>{{ fmtSeen(u.last_login_at) }}</div>
                  <div class="sub">建立 {{ u.created_at.slice(0, 10) }}</div>
                </td>
                <td class="acts-cell">
                  <div class="acts acts-wrap">
                    <!-- 三級配色（批81 需求 8，裁示 11）：導覽／停用＝secondary（中性有框）；
                         強制登出會讓對方當場掉線＝warn；刪除不可逆＝danger。
                         操作紀錄是 RouterLink，直接套全域 `.btn secondary sm` 與旁邊同高 28px。 -->
                    <RouterLink
                      class="btn secondary sm"
                      :to="{ name: 'audit', query: { actor: u.email } }"
                    >
                      操作紀錄
                    </RouterLink>
                    <UiButton
                      variant="warn"
                      size="sm"
                      :loading="busyLogout === u.id"
                      @click="forceLogout(u)"
                    >
                      強制登出
                    </UiButton>
                    <template v-if="u.email !== myEmail && canEditRow(u)">
                      <UiButton variant="secondary" size="sm" @click="openEdit(u)">編輯</UiButton>
                      <UiButton variant="secondary" size="sm" @click="deactivate(u)">停用</UiButton>
                      <UiButton variant="danger" size="sm" @click="remove(u)">刪除</UiButton>
                    </template>
                    <!-- 批110：營運主管動不了我方員工那幾列——講一句話比讓按鈕消失好，
                         否則會以為是畫面壞了。 -->
                    <span v-else-if="u.email !== myEmail" class="me">僅超級管理者可操作</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- 編輯帳號（批107）。**沒有 email 欄**——它是 Google 登入的身分鍵，要換人請刪除後重建。 -->
    <UiModal
      :open="!!editing"
      width="560px"
      :title="editing ? `編輯帳號 — ${editing.email}` : ''"
      @close="editing = null"
    >
      <div v-if="editing" class="form">
        <label class="field">
          <span>顯示名稱</span>
          <input v-model="editForm.name" maxlength="120" placeholder="（未填）" />
        </label>
        <label class="field">
          <span>角色</span>
          <!-- staff ↔ reseller 不可互轉（後端 422）：代理商窗口只能唯讀顯示；
               編輯自己時也鎖住（後端對自我提權一律 403）。 -->
          <select
            v-if="!isEditReseller && !isEditSelf"
            v-model="editForm.role"
          >
            <option v-for="o in STAFF_ROLE_OPTIONS" :key="o.value" :value="o.value">
              {{ o.label }}
            </option>
          </select>
          <input
            v-else
            :value="roleLabel(editForm.role)"
            disabled
            :title="
              isEditSelf
                ? '不能變更自己的角色'
                : '代理商與我方角色不可互轉——要改請刪除後重建'
            "
          />
        </label>
        <label v-if="isEditReseller" class="field">
          <span>所屬代理商</span>
          <select v-model="editForm.reseller_id">
            <option v-for="r in store.resellers" :key="r.id" :value="r.id" :disabled="!r.active">
              {{ r.name }}{{ r.active ? '' : '（已停權）' }}
            </option>
          </select>
        </label>
      </div>

      <h4 v-if="editing" class="sub-title">產品範圍</h4>
      <ProductScopeField
        v-if="editing"
        v-model="editForm.product_keys"
        :is-reseller="isEditReseller"
        :company-keys="editCompany?.product_keys ?? null"
      />

      <label v-if="editing && !isEditSelf" class="chk">
        <input v-model="editForm.active" type="checkbox" />
        啟用中
        <span class="subtle">取消勾選＝停用這個帳號（他的 session 下次請求就失效）</span>
      </label>

      <template #foot>
        <UiButton @click="editing = null">取消</UiButton>
        <UiButton variant="primary" :loading="editSaving" @click="saveEdit">儲存</UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
.page-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.spacer {
  flex: 1;
}
.acts {
  display: flex;
  gap: 6px;
  align-items: center;
}
/* 批113 ⑨：已啟用表的操作欄五顆按鈕**排成兩列**（操作紀錄／強制登出 ‖ 編輯／停用／刪除）。
   一列排開是 323px，1366 下就是把其餘六欄擠爆的元凶；主人裁示不減按鈕、也不新做下拉元件，
   所以改成折行。
   ★ 選擇器必須帶 `.tbl` 前綴——共用層 `ui.css` 的 `.tbl .acts` 是 `flex-wrap: nowrap`（0,2,0），
     單寫 scoped 的 `.acts-wrap[data-v]` 同為 (0,2,0)，靠樣式注入順序決勝負太脆；
     `.tbl .acts-wrap[data-v]` 是 (0,3,0) 才穩穩蓋過去。
   ★ 寬度是 `width` 不是 `max-width`：`ui.css` 的 `.tbl td.acts-cell` 帶 `width: 1%`（縮到內容寬），
     只給上限的話這一格的 min-content ＝「最寬的那顆按鈕」69px，五顆會排成**五列**。
     190px 是量出來的：第一列兩顆約 138px、第二列三顆約 144px，再寬一點就變成「四顆＋一顆」。
   ★ 只掛在已啟用表（`.acts-wrap`）：待首登那張只有兩顆按鈕，跟著吃 190px 只是白佔 96px。 */
.tbl .acts-wrap {
  flex-wrap: wrap;
  row-gap: 4px;
  width: 190px;
}
.me {
  color: var(--fg-4);
  font-size: var(--fs-11);
}
/* 「這是你自己」的標記。原本是行內文字「（你）」，Email 欄一窄就被推到第二行；
   改成 inline-block 的 pill 並在 `.mail` 上關掉換行，整塊就會跟著 email 走。 */
.me-badge {
  display: inline-block;
  margin-left: 4px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--bg);
  color: var(--fg-3);
  font-size: var(--fs-11);
  vertical-align: middle;
}
/* Email／新增者。原本（無下限、可折）會被操作欄壓到 166px，一個位址折成兩三行。
   ★ 這裡**不用 `white-space: nowrap`**（計畫原文）而是「下限 ＋ `overflow-wrap: anywhere`」：
     nowrap 會把這一欄的 min-content 釘死在「最長那個位址的整串寬度」，
     碰到 40 字元以上的代理商信箱就整張表超過 1028px、又橫捲回去了（實測 1126px）。
     給下限 200px 保證常見長度（~21 字元，約 175px）排得下一行，超長的才折——
     折一個位址遠比整頁橫捲、右邊欄位看不到來得好。
   ★ 必須是 `anywhere` 不是 `break-word`：只有前者會把 min-content 降到單一字元，
     也就是只有前者真的能當安全閥（後者照樣用整串寬度算 min-content）。 */
.mail {
  min-width: 200px;
  overflow-wrap: anywhere;
}
/* 顯示名稱：不給下限的話會被操作欄壓到 80px，兩個字的名字都能折成兩行。 */
.name-cell {
  min-width: 110px;
  white-space: nowrap;
}
/* 最後登入・建立時間（批113 ⑨ 合欄）：兩行都是固定長度的時間戳，一律不折。 */
.when {
  white-space: nowrap;
}
/* 合欄的第二行（與 LicensesView 同一套寫法）。 */
.sub {
  margin-top: 2px;
  font-size: var(--fs-11);
  color: var(--fg-3);
}
.invite {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--sp-3);
  margin-top: var(--sp-2);
}
.hf {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.hf input {
  min-width: 220px;
}
/* 可就地改名的欄位：原本框是透明的 dashed，滑過去才浮現＝看不出可以點。
   改成常駐一圈淡框（`--border-2`），hover 才轉強（批81 需求 8）。 */
.name-btn {
  border: 1px solid var(--border-2);
  border-radius: var(--r-sm);
  background: none;
  padding: 2px 6px;
  color: var(--fg-2);
  font-family: inherit;
  font-size: var(--fs-13);
  cursor: text;
  text-align: left;
}
.name-btn:hover {
  border-color: var(--border-strong);
  background: var(--bg-hover);
}
.name-in {
  width: 140px;
}
.nowrap {
  white-space: nowrap;
}
/* 批93-C：代理商公司／產品範圖欄（可能很長，限寬並縮字）。
   批113 ⑨ 併成一欄兩行後補 `min-width`：不給下限時它會被操作欄擠到 80px，
   「沿用公司：愛+1 社群工作台」就折成四行。 */
.scope {
  min-width: 170px;
  max-width: 240px;
  font-size: var(--fs-12);
  color: var(--fg-2);
}
.scope-line {
  overflow-wrap: anywhere;
}
.rs-hint {
  margin: var(--sp-2) 0 0;
  line-height: var(--lh-base);
}
/* 產品範圖勾選（膠囊式，與 ResellersView／ModulePicker 同一套視覺） */
.scope-block {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-2);
  margin-top: var(--sp-3);
  padding-top: var(--sp-3);
  border-top: 1px solid var(--border-2);
}
.scope-label {
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.pick {
  display: inline-flex;
  align-items: center;
  padding: 3px 11px;
  border: 1px solid var(--border-strong);
  border-radius: 999px;
  background: var(--bg);
  color: var(--fg-2);
  font-size: var(--fs-12);
  cursor: pointer;
  user-select: none;
}
.pick:hover {
  border-color: var(--brand-400);
}
.pick input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}
.pick.on {
  background: var(--brand-50);
  border-color: var(--brand-400);
  color: var(--brand-700);
  font-weight: 600;
}
/* ★ 不可叫 `.warn`（scoped 樣式會套到 UiButton variant="warn" 的根節點）。 */
.warn-text {
  margin: var(--sp-2) 0 0;
  color: var(--warning);
  font-size: var(--fs-12);
}
/* --- 編輯 modal（批107）--- */
.sub-title {
  margin: var(--sp-4) 0 var(--sp-2);
  font-size: var(--fs-13);
  font-weight: 600;
  color: var(--fg);
}
.chk {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: var(--sp-4);
  padding-top: var(--sp-3);
  border-top: 1px solid var(--border-2);
  font-size: var(--fs-13);
  color: var(--fg);
}
.chk .subtle {
  font-size: var(--fs-12);
  color: var(--fg-3);
}
/* 手機（批171）：勾選標籤與長說明擠在同一列時「啟用中」被壓成兩行 → 說明換到下一列。 */
@media (--mobile) {
  .chk {
    flex-wrap: wrap;
  }
  .chk .subtle {
    flex: 1 1 100%;
  }
}
</style>
